/**
 * This hook is used to update a CSS variable with the height of an element,
 * useful in instances where the height of an element is dynamic and needs
 * to be used in other parts of the application.
 */

import { debounce } from 'lodash';
import { useEffect } from 'react';

const useUpdateElementTotalHeightCssVar = (
  elementRef: React.MutableRefObject<HTMLElement | null>,
  cssVarName: string,
) => {
  useEffect(() => {
    const updateCssVar = (element: HTMLElement) => {
      const style = getComputedStyle(element);
      const paddingTop = parseFloat(style.paddingTop);
      const paddingBottom = parseFloat(style.paddingBottom);
      const borderTop = parseFloat(style.borderTopWidth);
      const borderBottom = parseFloat(style.borderBottomWidth);

      const heightWithPaddingAndBorder = element.clientHeight + paddingTop + paddingBottom + borderTop + borderBottom;

      document.body.style.setProperty(cssVarName, `${heightWithPaddingAndBorder}px`);
    };

    const currentElement = elementRef.current;

    if (currentElement) {
      // Initial update
      updateCssVar(currentElement);

      if ('ResizeObserver' in window) {
        const ro = new ResizeObserver(
          debounce((entries) => {
            entries.forEach((entry: ResizeObserverEntry) => {
              updateCssVar(entry.target as HTMLElement);
            });
          }, 200),
        );

        // Observe the referenced element
        ro.observe(currentElement);

        // Clean up the observer when the component unmounts
        return () => {
          ro.unobserve(currentElement);
        };
      }
    }

    return () => {};
  }, [elementRef, cssVarName]);
};

export { useUpdateElementTotalHeightCssVar };
